/**
 * Pure Vehicle Field Mapping source/fallback helpers.
 *
 * Reconciliation copies the current main `handleChangeSource` value rules.
 * Callers own Vue state: this module never imports Vue or mutates `modifiedFields`.
 */

export type FieldMappingSourceItem = {
  custom?: boolean
  __group?: boolean
  name?: string
  sources?: unknown[]
  values?: { source?: string; fallback?: Array<{ source?: string }> }
  policy?: { default_value?: unknown }
  [key: string]: unknown
}

export type ReconciledPrimarySource = {
  source: string
  fallback: Array<{ source: string }>
}

export type LocalDirtyComparison = 'clean' | 'dirty'

export function fieldSourceIds(item: FieldMappingSourceItem | null | undefined): string[] {
  const sources = item?.sources
  if (!Array.isArray(sources)) return []
  return sources
    .map((source: unknown) => {
      if (typeof source === 'string') return source
      if (source && typeof source === 'object' && 'id' in source) {
        return String((source as { id?: unknown }).id || '')
      }
      return ''
    })
    .filter(Boolean)
}

export function currentPrimaryId(item: FieldMappingSourceItem | null | undefined): string {
  return String(item?.values?.source || '')
}

export function currentFallbackId(item: FieldMappingSourceItem | null | undefined): string {
  return String(item?.values?.fallback?.[0]?.source || '')
}

/**
 * Current main Primary change semantics:
 * - collision (fallback === selected) → first remaining catalog source
 * - fallback set but not in remaining → clear
 * - empty fallback → keep empty
 * - no remaining sources → clear fallback
 */
export function reconcilePrimarySource(params: {
  fieldSources: string[]
  currentFallback: string
  selectedSource: string
}): ReconciledPrimarySource {
  const selectedSource = String(params.selectedSource || '')
  const fieldSources = Array.isArray(params.fieldSources) ? params.fieldSources.filter(Boolean) : []
  const availableFallbacks = fieldSources.filter((sourceId) => sourceId !== selectedSource)
  const currentFallback = String(params.currentFallback || '')

  let fallback: Array<{ source: string }> = currentFallback ? [{ source: currentFallback }] : []

  if (availableFallbacks.length > 0) {
    if (currentFallback === selectedSource) {
      fallback = [{ source: availableFallbacks[0] }]
    } else if (currentFallback && !availableFallbacks.includes(currentFallback)) {
      fallback = []
    }
  } else {
    fallback = []
  }

  return {
    source: selectedSource,
    fallback,
  }
}

function isSkippableBulkRow(item: FieldMappingSourceItem | null | undefined): boolean {
  if (!item) return true
  if (item.__group) return true
  if (item.custom) return true
  return false
}

export function isBulkPrimaryApplicable(
  item: FieldMappingSourceItem | null | undefined,
  selectedSource: string,
): boolean {
  const sourceId = String(selectedSource || '')
  if (!sourceId || isSkippableBulkRow(item)) return false
  if (!fieldSourceIds(item).includes(sourceId)) return false
  return currentPrimaryId(item) !== sourceId
}

export function isBulkFallbackApplicable(
  item: FieldMappingSourceItem | null | undefined,
  selectedSource: string,
): boolean {
  const sourceId = String(selectedSource || '')
  if (!sourceId || isSkippableBulkRow(item)) return false
  const primary = currentPrimaryId(item)
  if (!primary) return false
  if (sourceId === primary) return false
  if (!fieldSourceIds(item).includes(sourceId)) return false
  return currentFallbackId(item) !== sourceId
}

export function collectNormalizedCatalogSourceIds(
  fields: Array<FieldMappingSourceItem | null | undefined> | null | undefined,
): string[] {
  const ids = new Set<string>()
  for (const field of fields || []) {
    if (!field || field.__group || field.custom) continue
    for (const sourceId of fieldSourceIds(field)) {
      ids.add(sourceId)
    }
  }
  return Array.from(ids)
}

function trimmedDefaultValue(item: FieldMappingSourceItem | null | undefined): string {
  const raw = item?.policy?.default_value
  if (raw === null || raw === undefined) return ''
  return String(raw).trim()
}

export function compareFieldMappingLocalDirty(
  item: FieldMappingSourceItem | null | undefined,
  originalField: FieldMappingSourceItem | null | undefined,
): LocalDirtyComparison {
  if (!item || !originalField) return 'dirty'

  const sourceMatch = currentPrimaryId(item) === currentPrimaryId(originalField)
  const fallbackMatch = currentFallbackId(item) === currentFallbackId(originalField)
  const defaultMatch = trimmedDefaultValue(item) === trimmedDefaultValue(originalField)

  return sourceMatch && fallbackMatch && defaultMatch ? 'clean' : 'dirty'
}
